<script setup>
import { onMounted, ref, computed } from 'vue'
import { api } from '../../services/api'
import { useToast } from 'vuestic-ui'
import { useCompanyStore } from '../../stores/companies'
import { useAppStore } from '../../stores/appStore'
import UserDataTable from '../../components/reports/UserDataTable.vue'
import CompanyDataTable from '../../components/reports/CompanyDataTable.vue'
import OrderDataTable from '../../components/reports/OrderDataTable.vue'
import ProfessionalDataTable from '../../components/reports/ProfessionalDataTable.vue'

const { init: notify } = useToast()

const companyStore = useCompanyStore()
const appStore = useAppStore()

const companiesOptions = computed(() => {
  if (!Array.isArray(companyStore.companies)) {
    return []
  }
  return companyStore.companies.map((company) => ({
    text: `${company.name} ${!company.active ? '(Inativo)' : ''}`,
    value: company.id,
  }))
})

const filters = ref({
  type: 'order',
  tenant_id: null,
  start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
  end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString(),
})

const data = ref({
  type: '',
  start_date: null,
  end_date: null,
  logs: [],
})

function formatDateValue(date) {
  if (!date) return ''
  return date.toISOString()
}

async function generateReport() {
  appStore.toggleLoading(true)
  try {
    const response = await api.get('/reports', {
      params: {
        type: filters.value.type,
        tenant_id: filters.value.tenant_id || null,
        start_date: filters.value.start_date ? filters.value.start_date.split('T')[0] : null,
        end_date: filters.value.end_date ? filters.value.end_date.split('T')[0] : null,
      },
    })

    notify({
      color: 'success',
      message: 'Relatório gerado com sucesso!',
    })

    data.value = response.data.data
  } catch (error) {
    console.error(error)
    if (error?.response && error.response.data && error.response.data.options) {
      Object.entries(error.response.data.options).forEach(([key, messages]) => {
        messages.forEach((message) => {
          notify({
            color: 'danger',
            message: message,
          })
        })
      })
      return
    }
    notify({
      color: 'danger',
      message: 'Erro ao gerar relatório.',
    })
  } finally {
    appStore.toggleLoading(false)
  }
}

onMounted(async () => {
  await companyStore.getCompanies(true)
})
</script>

<template>
  <div>
    <div class="flex items-center gap-4 flex-wrap mb-4">
      <h1 class="page-title !mb-0">Relatórios</h1>
    </div>

    <VaCard class="mb-4">
      <VaCardContent>
        <h5 class="font-bold text-xl mb-4">Filtros</h5>
        <form class="grid grid-cols-6 items-end gap-4" @submit.prevent="generateReport">
          <div>
            <VaSelect
              v-model="filters.type"
              label="Tipo de Relatório"
              track-by="text"
              value-by="value"
              :options="[
                { text: 'Pedidos', value: 'order' },
                // { text: 'Vendas', value: 'sale' },
                { text: 'Empresas', value: 'tenant' },
                { text: 'Usuários', value: 'customer' },
                { text: 'Profissionais', value: 'professional' },
                // { text: 'Produtos', value: 'product' },
                // { text: 'Consultas', value: 'consultation' },
              ]"
              searchable
              highlight-matched-text
            />
          </div>
          <div v-if="['customer'].includes(filters.type)">
            <VaSelect
              v-model="filters.tenant_id"
              label="Usuários da Empresa"
              track-by="text"
              value-by="value"
              :options="companiesOptions"
              searchable
              highlight-matched-text
              clearable
            />
          </div>
          <div>
            <VaDateInput
              v-model="filters.start_date"
              class="w-full"
              label="Data Início"
              :format-value="formatDateValue"
            />
          </div>
          <div>
            <VaDateInput v-model="filters.end_date" class="w-full" label="Data Fim" :format-value="formatDateValue" />
          </div>
          <div>
            <VaButton :disabled="appStore.loading" type="submit" color="primary" class="w-full">
              <span v-if="appStore.loading">Gerando...</span>
              <span v-else>Gerar Relatório</span>
            </VaButton>
          </div>
        </form>

        <!-- Usuários -->
        <!-- Usuários -->
        <!-- Usuários -->
        <UserDataTable
          v-if="data.logs.length && data.type === 'customer'"
          :data="data.logs"
          :start-date="data.start_date"
          :end-date="data.end_date"
          class="mt-6"
          :companies="companyStore.companies"
        />

        <div v-else-if="data.type === 'customer' && !data.logs.length">
          <p class="text-center text-secondary mt-6">Nenhum dado para exibir.</p>
        </div>
        <!-- Usuários -->
        <!-- Usuários -->
        <!-- Usuários -->

        <!-- Profissionais -->
        <!-- Profissionais -->
        <!-- Profissionais -->
        <ProfessionalDataTable
          v-if="data.logs.length && data.type === 'professional'"
          :data="data.logs"
          :start-date="data.start_date"
          :end-date="data.end_date"
          class="mt-6"
          :companies="companyStore.companies"
        />

        <div v-else-if="data.type === 'professional' && !data.logs.length">
          <p class="text-center text-secondary mt-6">Nenhum dado para exibir.</p>
        </div>
        <!-- Profissionais -->
        <!-- Profissionais -->
        <!-- Profissionais -->

        <!-- Empresas -->
        <!-- Empresas -->
        <!-- Empresas -->
        <CompanyDataTable
          v-if="data.logs.length && data.type === 'tenant'"
          :data="data.logs"
          :start-date="data.start_date"
          :end-date="data.end_date"
          class="mt-6"
          :companies="companyStore.companies"
        />

        <div v-else-if="data.type === 'tenant' && !data.logs.length">
          <p class="text-center text-secondary mt-6">Nenhum dado para exibir.</p>
        </div>
        <!-- Empresas -->
        <!-- Empresas -->
        <!-- Empresas -->

        <!-- Pedidos -->
        <!-- Pedidos -->
        <!-- Pedidos -->
        <OrderDataTable
          v-if="data.logs.length && data.type === 'order'"
          :data="data.logs"
          :start-date="data.start_date"
          :end-date="data.end_date"
          class="mt-6"
          :companies="companyStore.companies"
        />

        <div v-else-if="data.type === 'order' && !data.logs.length">
          <p class="text-center text-secondary mt-6">Nenhum dado para exibir.</p>
        </div>
        <!-- Pedidos -->
        <!-- Pedidos -->
        <!-- Pedidos -->
      </VaCardContent>
    </VaCard>
  </div>
</template>

<style scoped></style>
